var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import React, { useRef, useState, useEffect } from 'react';
/* eslint-disable no-restricted-imports */
import { Animated, PanResponder, StyleSheet, Text, View, useWindowDimensions, Switch, ScrollView, Modal, TouchableOpacity, TouchableWithoutFeedback, Clipboard, Alert, } from 'react-native';
import Config from 'react-native-config';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDevToolConfig } from './useDevTool';
import { EventLoggingBottomSheet } from './EventLoggingBottomSheet';
import { APILoggingBottomSheet } from './APILoggingBottomSheet';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { loggedEventsAtom, apiLogsAtom, initializeAPILoggingDevTool, } from './devToolEventStore';
import { initializeEventLoggingDevTool } from '@/logging/Logger';
import { accessTokenAtom, experimentAtom, experimentOverrideAtom, useMe, } from '@/atoms/Auth';
/**
 * Determines whether DevTool should be shown based on environment
 * @returns true if DevTool should be visible (local dev or sandbox with ENABLE_DEVTOOL=true)
 */
export const shouldShowDevTool = () => {
    return Config.ENABLE_DEVTOOL === 'true';
};
export const DevTool = () => {
    const { width: screenWidth, height: screenHeight } = useWindowDimensions();
    const insets = useSafeAreaInsets();
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
    const [isEventLoggingSheetOpen, setIsEventLoggingSheetOpen] = useState(false);
    const [isAPILoggingSheetOpen, setIsAPILoggingSheetOpen] = useState(false);
    const [isExperimentModalOpen, setIsExperimentModalOpen] = useState(false);
    const [config, setConfig] = useDevToolConfig();
    const setLoggedEvents = useSetAtom(loggedEventsAtom);
    const setAPILogs = useSetAtom(apiLogsAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const { userInfo } = useMe();
    const experiments = useAtomValue(experimentAtom);
    const [experimentOverrides, setExperimentOverrides] = useAtom(experimentOverrideAtom);
    // 서버에서 내려준 실험 목록 (통합 atom 기반)
    const knownExperiments = Object.keys(experiments !== null && experiments !== void 0 ? experiments : {});
    const getEffectiveVariant = (experimentName) => {
        var _a, _b;
        if (experimentOverrides[experimentName]) {
            return experimentOverrides[experimentName];
        }
        return (_b = (_a = experiments === null || experiments === void 0 ? void 0 : experiments[experimentName]) === null || _a === void 0 ? void 0 : _a.variant) !== null && _b !== void 0 ? _b : 'CONTROL';
    };
    const handleExperimentToggle = (experimentName) => {
        var _a, _b;
        const variants = (_b = (_a = experiments === null || experiments === void 0 ? void 0 : experiments[experimentName]) === null || _a === void 0 ? void 0 : _a.availableVariants) !== null && _b !== void 0 ? _b : [
            'CONTROL',
            'TREATMENT_1',
        ];
        const currentVariant = getEffectiveVariant(experimentName);
        const currentIndex = variants.indexOf(currentVariant);
        const newVariant = variants[(currentIndex + 1) % variants.length];
        setExperimentOverrides(prev => (Object.assign(Object.assign({}, prev), { [experimentName]: newVariant })));
    };
    const handleResetExperimentOverride = (experimentName) => {
        setExperimentOverrides(prev => {
            const next = Object.assign({}, prev);
            delete next[experimentName];
            return next;
        });
    };
    // Initialize event logging and API logging (enabled in dev or sandbox)
    useEffect(() => {
        if (shouldShowDevTool()) {
            initializeEventLoggingDevTool(setLoggedEvents);
            initializeAPILoggingDevTool(setAPILogs);
        }
    }, []);
    // Floating button position
    const pan = useRef(new Animated.ValueXY({
        x: screenWidth - 70,
        y: screenHeight - 150 - insets.bottom,
    })).current;
    // Track movement distance to distinguish tap from drag
    const moveDistance = useRef(0);
    const panResponder = useRef(PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderGrant: () => {
            moveDistance.current = 0;
            pan.extractOffset();
        },
        onPanResponderMove: (_, gestureState) => {
            moveDistance.current = Math.sqrt(gestureState.dx * gestureState.dx + gestureState.dy * gestureState.dy);
            Animated.event([null, { dx: pan.x, dy: pan.y }], {
                useNativeDriver: false,
            })(_, gestureState);
        },
        onPanResponderRelease: () => {
            pan.flattenOffset();
            // If movement is less than 10px, treat as tap
            if (moveDistance.current < 10) {
                toggleBottomSheet();
            }
        },
    })).current;
    const toggleBottomSheet = () => {
        setIsBottomSheetOpen(!isBottomSheetOpen);
    };
    const handleSearchRadiusToggle = (enabled) => {
        setConfig(prev => (Object.assign(Object.assign({}, prev), { searchRegion: Object.assign(Object.assign({}, prev.searchRegion), { enabled }) })));
    };
    const handleShowEventLogs = () => {
        setIsEventLoggingSheetOpen(true);
        setIsBottomSheetOpen(false);
    };
    const handleShowAPILogs = () => {
        setIsAPILoggingSheetOpen(true);
        setIsBottomSheetOpen(false);
    };
    const handleCopyAccessToken = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!accessToken) {
                Alert.alert('알림', 'Access Token이 없습니다. 로그인을 확인해주세요.');
                return;
            }
            yield Clipboard.setString(accessToken);
            Alert.alert('복사 완료', 'Access Token이 클립보드에 복사되었습니다.');
        }
        catch (_) {
            Alert.alert('오류', 'Access Token 복사에 실패했습니다.');
        }
    });
    const handleCopyUserId = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!(userInfo === null || userInfo === void 0 ? void 0 : userInfo.id)) {
                Alert.alert('알림', 'User ID가 없습니다. 로그인을 확인해주세요.');
                return;
            }
            yield Clipboard.setString(userInfo.id);
            Alert.alert('복사 완료', 'User ID가 클립보드에 복사되었습니다.');
        }
        catch (_) {
            Alert.alert('오류', 'User ID 복사에 실패했습니다.');
        }
    });
    if (!shouldShowDevTool()) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(Animated.View, Object.assign({ style: [
                    styles.floatingButton,
                    styles.floatingButtonTouchable,
                    {
                        transform: pan.getTranslateTransform(),
                    },
                ] }, panResponder.panHandlers, { children: _jsx(Text, { style: styles.floatingButtonText, children: "DEV" }) })), _jsx(Modal, { visible: isBottomSheetOpen, animationType: "none", transparent: true, statusBarTranslucent: true, onRequestClose: () => setIsBottomSheetOpen(false), children: _jsx(TouchableWithoutFeedback, { onPress: () => setIsBottomSheetOpen(false), children: _jsx(View, { style: styles.modalContainer, children: _jsx(TouchableWithoutFeedback, { onPress: () => { }, children: _jsxs(View, { style: styles.bottomSheet, children: [_jsxs(View, { style: styles.bottomSheetHeader, children: [_jsx(Text, { style: styles.bottomSheetTitle, children: "\uAC1C\uBC1C\uC790 \uB3C4\uAD6C \uC124\uC815" }), _jsx(TouchableOpacity, { onPress: toggleBottomSheet, children: _jsx(Text, { style: styles.closeButton, children: "\u2715" }) })] }), _jsxs(ScrollView, { style: styles.bottomSheetContent, contentContainerStyle: { paddingBottom: insets.bottom + 24 }, children: [_jsxs(View, { style: styles.settingRow, children: [_jsxs(View, { style: styles.settingInfo, children: [_jsx(Text, { style: styles.settingLabel, children: "\uAC80\uC0C9 \uBC94\uC704 \uD45C\uC2DC" }), _jsx(Text, { style: styles.settingDescription, children: "\uC9C0\uB3C4\uC5D0\uC11C \uC7A5\uC18C\uB97C \uAC80\uC0C9\uD560 \uB54C \uAC80\uC0C9 \uBC94\uC704\uB97C \uC6D0\uC73C\uB85C \uD45C\uC2DC" })] }), _jsx(Switch, { value: config.searchRegion.enabled, onValueChange: handleSearchRadiusToggle, trackColor: { false: '#767577', true: '#81b0ff' }, thumbColor: config.searchRegion.enabled ? '#f5dd4b' : '#f4f3f4' })] }), _jsxs(TouchableOpacity, { style: styles.actionRow, onPress: handleShowEventLogs, children: [_jsxs(View, { style: styles.settingInfo, children: [_jsx(Text, { style: styles.settingLabel, children: "GA \uC774\uBCA4\uD2B8" }), _jsx(Text, { style: styles.settingDescription, children: "GA\uC5D0 \uCC0D\uD788\uB294 \uD074\uB77C\uC774\uC5B8\uD2B8 \uC774\uBCA4\uD2B8 \uD655\uC778" })] }), _jsx(View, { style: styles.actionButton, children: _jsx(Text, { style: styles.actionButtonText, children: "\uC774\uBCA4\uD2B8 \uBCF4\uAE30" }) })] }), _jsxs(TouchableOpacity, { style: styles.actionRow, onPress: handleShowAPILogs, children: [_jsxs(View, { style: styles.settingInfo, children: [_jsx(Text, { style: styles.settingLabel, children: "\uC11C\uBC84 \uD1B5\uC2E0 \uB85C\uADF8" }), _jsx(Text, { style: styles.settingDescription, children: "\uC571\uACFC \uC11C\uBC84 \uAC04 \uB370\uC774\uD130 \uC1A1\uC218\uC2E0 \uB0B4\uC5ED \uD655\uC778" })] }), _jsx(View, { style: styles.actionButton, children: _jsx(Text, { style: styles.actionButtonText, children: "\uB85C\uADF8 \uBCF4\uAE30" }) })] }), _jsxs(TouchableOpacity, { style: styles.actionRow, onPress: handleCopyAccessToken, children: [_jsxs(View, { style: styles.settingInfo, children: [_jsx(Text, { style: styles.settingLabel, children: "\uB85C\uADF8\uC778 \uD1A0\uD070 \uBCF5\uC0AC" }), _jsx(Text, { style: styles.settingDescription, children: "\uD604\uC7AC \uB85C\uADF8\uC778 \uC815\uBCF4\uB97C \uD074\uB9BD\uBCF4\uB4DC\uC5D0 \uBCF5\uC0AC (\uAC1C\uBC1C\uC790\uC6A9)" })] }), _jsx(View, { style: styles.actionButton, children: _jsx(Text, { style: styles.actionButtonText, children: "\uBCF5\uC0AC\uD558\uAE30" }) })] }), _jsxs(TouchableOpacity, { style: styles.actionRow, onPress: handleCopyUserId, children: [_jsxs(View, { style: styles.settingInfo, children: [_jsx(Text, { style: styles.settingLabel, children: "User ID \uBCF5\uC0AC" }), _jsx(Text, { style: styles.settingDescription, children: "\uD604\uC7AC \uC0AC\uC6A9\uC790 ID\uB97C \uD074\uB9BD\uBCF4\uB4DC\uC5D0 \uBCF5\uC0AC (\uAC1C\uBC1C\uC790\uC6A9)" })] }), _jsx(View, { style: styles.actionButton, children: _jsx(Text, { style: styles.actionButtonText, children: "\uBCF5\uC0AC\uD558\uAE30" }) })] }), _jsxs(TouchableOpacity, { style: styles.actionRow, onPress: () => {
                                                    setIsExperimentModalOpen(true);
                                                    setIsBottomSheetOpen(false);
                                                }, children: [_jsxs(View, { style: styles.settingInfo, children: [_jsx(Text, { style: styles.settingLabel, children: "\uC2E4\uD5D8 \uAD00\uB9AC" }), _jsx(Text, { style: styles.settingDescription, children: "A/B \uD14C\uC2A4\uD2B8 variant \uD655\uC778 \uBC0F \uC624\uBC84\uB77C\uC774\uB4DC" })] }), _jsx(View, { style: styles.actionButton, children: _jsx(Text, { style: styles.actionButtonText, children: "\uAD00\uB9AC\uD558\uAE30" }) })] })] })] }) }) }) }) }), _jsx(EventLoggingBottomSheet, { visible: isEventLoggingSheetOpen, onClose: () => setIsEventLoggingSheetOpen(false) }), _jsx(APILoggingBottomSheet, { visible: isAPILoggingSheetOpen, onClose: () => setIsAPILoggingSheetOpen(false) }), _jsx(Modal, { visible: isExperimentModalOpen, animationType: "none", transparent: true, statusBarTranslucent: true, onRequestClose: () => setIsExperimentModalOpen(false), children: _jsx(TouchableWithoutFeedback, { onPress: () => setIsExperimentModalOpen(false), children: _jsx(View, { style: styles.modalContainer, children: _jsx(TouchableWithoutFeedback, { onPress: () => { }, children: _jsxs(View, { style: styles.bottomSheet, children: [_jsxs(View, { style: styles.bottomSheetHeader, children: [_jsx(Text, { style: styles.bottomSheetTitle, children: "\uC2E4\uD5D8 (A/B \uD14C\uC2A4\uD2B8)" }), _jsx(TouchableOpacity, { onPress: () => setIsExperimentModalOpen(false), children: _jsx(Text, { style: styles.closeButton, children: "\u2715" }) })] }), _jsxs(ScrollView, { style: styles.bottomSheetContent, contentContainerStyle: { paddingBottom: insets.bottom + 24 }, children: [knownExperiments.length === 0 && (_jsx(View, { style: styles.experimentRow, children: _jsx(Text, { style: styles.settingDescription, children: "\uC2E4\uD5D8 \uC5C6\uC74C (\uC11C\uBC84\uC5D0\uC11C \uBC30\uC815\uB41C \uC2E4\uD5D8\uC774 \uC5C6\uC2B5\uB2C8\uB2E4)" }) })), knownExperiments.map(experimentName => {
                                                var _a, _b;
                                                const serverVariant = (_b = (_a = experiments === null || experiments === void 0 ? void 0 : experiments[experimentName]) === null || _a === void 0 ? void 0 : _a.variant) !== null && _b !== void 0 ? _b : '(없음)';
                                                const effectiveVariant = getEffectiveVariant(experimentName);
                                                const hasOverride = experimentOverrides[experimentName] !== undefined;
                                                const isNonControl = effectiveVariant !== 'CONTROL';
                                                return (_jsxs(View, { style: styles.experimentRow, children: [_jsxs(View, { style: styles.experimentInfo, children: [_jsx(Text, { style: styles.settingLabel, children: experimentName }), _jsxs(Text, { style: styles.settingDescription, children: ["\uC11C\uBC84 \uBC30\uC815: ", serverVariant, hasOverride
                                                                            ? ` | 오버라이드: ${effectiveVariant} (재시작 시 초기화)`
                                                                            : ''] })] }), _jsxs(View, { style: styles.experimentActions, children: [_jsx(TouchableOpacity, { style: [
                                                                        styles.experimentToggle,
                                                                        isNonControl && styles.experimentToggleActive,
                                                                    ], onPress: () => handleExperimentToggle(experimentName), children: _jsx(Text, { style: [
                                                                            styles.experimentToggleText,
                                                                            isNonControl &&
                                                                                styles.experimentToggleTextActive,
                                                                        ], children: effectiveVariant }) }), hasOverride && (_jsx(TouchableOpacity, { style: styles.experimentReset, onPress: () => handleResetExperimentOverride(experimentName), children: _jsx(Text, { style: styles.experimentResetText, children: "\uCD08\uAE30\uD654" }) }))] })] }, experimentName));
                                            })] })] }) }) }) }) })] }));
};
const styles = StyleSheet.create({
    floatingButton: {
        position: 'absolute',
        zIndex: 10000,
    },
    floatingButtonTouchable: {
        width: 50,
        height: 50,
        borderRadius: 25,
        backgroundColor: 'rgba(255, 87, 34, 0.9)',
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
    },
    floatingButtonText: {
        color: 'white',
        fontSize: 12,
        fontWeight: 'bold',
    },
    modalContainer: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'flex-end',
    },
    bottomSheet: {
        backgroundColor: 'white',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        maxHeight: '70%',
        minHeight: '50%',
    },
    bottomSheetHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    bottomSheetTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: '#333',
    },
    closeButton: {
        fontSize: 24,
        color: '#666',
    },
    bottomSheetContent: {
        flex: 1,
        padding: 16,
    },
    settingRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    settingInfo: {
        flex: 1,
        marginRight: 12,
    },
    settingLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: '#333',
        marginBottom: 4,
    },
    settingDescription: {
        fontSize: 12,
        color: '#666',
    },
    actionRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    actionButton: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        backgroundColor: '#2196F3',
        borderRadius: 6,
    },
    actionButtonText: {
        color: 'white',
        fontSize: 14,
        fontWeight: '600',
    },
    sectionHeader: {
        marginTop: 16,
        marginBottom: 8,
        paddingBottom: 8,
        borderBottomWidth: 2,
        borderBottomColor: '#333',
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#333',
    },
    experimentRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    experimentInfo: {
        flex: 1,
        marginRight: 12,
    },
    experimentActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    experimentToggle: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        backgroundColor: '#e0e0e0',
        borderRadius: 6,
        minWidth: 100,
        alignItems: 'center',
    },
    experimentToggleActive: {
        backgroundColor: '#4CAF50',
    },
    experimentToggleText: {
        fontSize: 12,
        fontWeight: '600',
        color: '#333',
    },
    experimentToggleTextActive: {
        color: 'white',
    },
    experimentReset: {
        paddingHorizontal: 8,
        paddingVertical: 6,
        backgroundColor: '#ff9800',
        borderRadius: 6,
    },
    experimentResetText: {
        fontSize: 12,
        fontWeight: '600',
        color: 'white',
    },
});
export default DevTool;
